<template>
  <HrPageLayout
    title="Leave Balances Management"
    subtitle="Manage employee leave balances and allocations"
    icon="📊"
    :breadcrumb-items="[
      { label: 'Leave & Attendance' },
      { label: 'Leave Balances' }
    ]"
  >
    <template #header-actions>
      <select class="hr-select" style="width: 120px;" v-model="selectedYear" @change="fetchEmployees">
        <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
      </select>
      <select class="hr-select" style="width: 180px;" v-model="selectedDepartment" @change="fetchEmployees">
        <option value="">All Departments</option>
        <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.departmentName }}</option>
      </select>
      <select class="hr-select" style="width: 180px;" v-model="initStatusFilter" @change="fetchEmployees">
        <option value="">All Status</option>
        <option value="not_initialized">Not Initialized</option>
        <option value="partial">Partially Initialized</option>
        <option value="initialized">Fully Initialized</option>
      </select>
      <button class="hr-btn hr-btn-primary" @click="showBulkInitializeModal()" :disabled="selectedEmployees.length === 0">
        <i class="bx bx-plus-circle"></i> Bulk Initialize ({{ selectedEmployees.length }})
      </button>
      <button class="hr-btn hr-btn-info" @click="openCarryForwardModal()" :disabled="selectedEmployees.length === 0">
        <i class="bx bx-transfer"></i> Carry Forward
      </button>
      <button class="hr-btn hr-btn-warning" @click="showCashOutModal()" :disabled="selectedEmployees.length === 0">
        <i class="bx bx-money"></i> Cash Out
      </button>
      <button class="hr-btn hr-btn-success" @click="downloadReport" :disabled="downloadLoading">
        <i class="bx bx-download"></i> {{ downloadLoading ? 'Downloading...' : 'Export' }}
      </button>
    </template>

    <div class="leave-balances-content">
      <!-- Stats -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card" :class="'gradient-' + (statsCards.indexOf(stat) + 1)" v-for="stat in statsCards" :key="stat.key">
          <div class="hr-metric-icon"><i :class="'bx ' + stat.icon"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">{{ stat.label }}</p>
          <h2 class="hr-metric-value">{{ metrics[stat.key] || 0 }}</h2>
            </div>
        </div>
      </div>

      <!-- Main Table Card -->
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-list-ul"></i> Employee Leave Balances</h5>
          <div class="hr-search-wrap" style="min-width: 260px;">
            <input v-model="searchValue" type="text" class="hr-input" placeholder="Search by name, emp no, or ID..." @keyup.enter="fetchEmployees">
            <i class="bx bx-search-alt search-icon"></i>
          </div>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-state">
            <div class="hr-spinner"></div>
            <p>Loading employee leave balances...</p>
          </div>

          <div v-else-if="employees.length === 0" class="hr-empty-state">
            <i class="bx bx-user-x"></i>
            <h5>No employees found</h5>
            <p>Try adjusting your filters</p>
          </div>

          <template v-else>
            <div class="hr-table-wrapper">
              <table class="hr-table">
                <thead>
                  <tr>
                    <th style="width: 40px;">
                      <input type="checkbox" class="hr-checkbox" :checked="isAllSelected" @change="toggleSelectAll">
                    </th>
                    <th>Employee</th>
                    <th>Department</th>
                    <th style="text-align: center;">Total Days</th>
                    <th style="text-align: center;">Used</th>
                    <th style="text-align: center;">Remaining</th>
                    <th style="min-width: 200px;">Consumption</th>
                    <th>Status</th>
                    <th style="text-align: center;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="emp in employees" :key="emp.nationalId">
                    <td>
                      <input type="checkbox" class="hr-checkbox" :value="emp.nationalId" v-model="selectedEmployees">
                    </td>
                    <td>
                      <div class="hr-flex hr-flex-center hr-gap-2">
                        <div class="avatar-initials">{{ getInitials(emp.fullName) }}</div>
                        <div>
                          <div class="emp-name">{{ emp.fullName }}</div>
                          <small class="emp-meta">{{ emp.empNo }} | {{ emp.nationalId }}</small>
                        </div>
                      </div>
                    </td>
                    <td><span class="hr-badge hr-badge-muted">{{ emp.departmentName }}</span></td>
                    <td style="text-align: center;"><span class="text-primary-bold">{{ emp.totalLeaveDays || 0 }}</span></td>
                    <td style="text-align: center;"><span class="text-warning-bold">{{ emp.usedLeaveDays || 0 }}</span></td>
                    <td style="text-align: center;"><span :class="getRemainingDaysClass(emp)">{{ emp.remainingLeaveDays || 0 }}</span></td>
                    <td>
                      <div class="consumption-wrapper">
                        <div class="hr-flex hr-flex-between hr-mb-1">
                          <small class="hr-text-muted">{{ getConsumptionPercent(emp) }}% used</small>
                          <small class="hr-text-muted">{{ emp.usedLeaveDays || 0 }}/{{ emp.totalLeaveDays || 0 }}</small>
                        </div>
                        <div class="hr-progress-bar">
                          <div class="hr-progress-fill" :class="getProgressBarClass(emp)" :style="{ width: getConsumptionPercent(emp) + '%' }"></div>
                        </div>
                      </div>
                    </td>
                    <td><span class="hr-badge" :class="getStatusClass(emp.initializationStatus)">{{ emp.initializationStatus }}</span></td>
                    <td>
                      <div class="hr-flex hr-flex-center hr-gap-2" style="justify-content: center;">
                        <button @click="viewEmployeeDetails(emp)" class="hr-btn hr-btn-info hr-btn-sm hr-btn-icon" title="View & Manage Balances"><i class="bx bx-show"></i></button>
                        <button v-if="emp.initializationStatus !== 'Initialized'" @click="initializeSingle(emp)" class="hr-btn hr-btn-success hr-btn-sm hr-btn-icon" :disabled="emp.processing" title="Initialize Balances"><i class="bx bx-check-square"></i></button>
                        <button @click="openAdjustModal(emp)" class="hr-btn hr-btn-warning hr-btn-sm hr-btn-icon" title="Quick Adjust"><i class="bx bx-edit"></i></button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="totalPages > 1" class="hr-pagination">
              <div class="hr-pagination-info">Showing {{ startIndex + 1 }} to {{ Math.min(endIndex, totalItems) }} of {{ totalItems }} entries</div>
              <nav class="hr-pagination-nav">
                <button class="hr-page-btn" :disabled="currentPage === 1" @click="prevPage">Previous</button>
                <template v-for="page in visiblePages" :key="page">
                  <button v-if="page !== '...'" class="hr-page-btn" :class="{ active: page === currentPage }" @click="goToPage(page)">{{ page }}</button>
                  <span v-else class="hr-page-btn ellipsis">...</span>
                </template>
                <button class="hr-page-btn" :disabled="currentPage === totalPages" @click="nextPage">Next</button>
              </nav>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Employee Leave Details Modal (Custom Vue Modal) -->
    <div v-if="showDetailsModal" class="custom-modal-overlay" @click.self="closeDetailsModal">
      <div class="custom-modal custom-modal-xl">
        <div class="custom-modal-header bg-primary text-white">
          <h5 class="custom-modal-title text-white">
            <i class="mdi mdi-account-details me-2"></i>
            Leave Balances - {{ selectedEmployee.fullName || selectedEmployee.firstName + ' ' + selectedEmployee.lastName }}
          </h5>
          <button type="button" class="custom-modal-close" @click="closeDetailsModal">×</button>
        </div>
        <div class="custom-modal-body">
          <!-- Employee Info Header -->
          <div class="row mb-4">
            <div class="col-md-3">
              <label class="text-muted small">Employee No</label>
              <p class="fw-medium mb-0">{{ selectedEmployee.empNo }}</p>
            </div>
            <div class="col-md-3">
              <label class="text-muted small">National ID</label>
              <p class="fw-medium mb-0">{{ selectedEmployee.nationalId }}</p>
            </div>
            <div class="col-md-3">
              <label class="text-muted small">Department</label>
              <p class="fw-medium mb-0">{{ selectedEmployee.departmentName }}</p>
            </div>
            <div class="col-md-3">
              <label class="text-muted small">Hire Date</label>
              <p class="fw-medium mb-0">{{ formatDate(selectedEmployee.hireDate) }}</p>
            </div>
          </div>

          <hr>

          <!-- Leave Balances Table -->
          <h6 class="text-primary mb-3">
            <i class="mdi mdi-calendar-check me-1"></i>
            Leave Balances for {{ selectedYear }}
          </h6>

          <div v-if="detailsLoading" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <span class="ms-2">Loading balances...</span>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-bordered table-striped">
              <thead class="table-light">
              <tr>
                <th>Leave Type</th>
                <th class="text-center">Rule</th>
                <th class="text-center">Allocated</th>
                <th class="text-center">Carried Over</th>
                <th class="text-center">Total Available</th>
                <th class="text-center">Used</th>
                <th class="text-center">Remaining</th>
                <th>Initialized By</th>
                <th>Carried Forward By</th>
                <th class="text-center">Status</th>
                <th class="text-center">Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="balance in employeeBalances" :key="balance.leaveTypeId">
                <td>
                  <strong>{{ balance.typeName }}</strong>
                  <br>
                  <small class="text-muted">Default: {{ balance.defaultAllocation }} days</small>
                </td>
                <td class="text-center">
                  <small class="d-block">{{ balance.calculationLabel || 'Fixed annual allocation' }}</small>
                  <span class="badge bg-light text-dark">{{ formatEligibleGender(balance.eligibleGender) }}</span>
                </td>
                <td class="text-center">{{ balance.totalDays || 0 }}</td>
                <td class="text-center">{{ balance.carriedOverDays || 0 }}</td>
                <td class="text-center fw-medium">
                  {{ (parseFloat(balance.totalDays || 0) + parseFloat(balance.carriedOverDays || 0)).toFixed(1) }}
                </td>
                <td class="text-center">
                  <span :class="balance.usedDays > 0 ? 'text-warning' : ''">
                    {{ balance.usedDays || 0 }}
                  </span>
                </td>
                <td class="text-center">
                  <span class="badge" :class="getRemainingClass(balance)">
                    {{ balance.remainingDays || 0 }}
                  </span>
                </td>
                <td>
                  <template v-if="balance.initializedByName || balance.initializedAt">
                    <div>{{ balance.initializedByName || balance.initializedBy || '-' }}</div>
                    <small class="text-muted">{{ formatDate(balance.initializedAt) }}</small>
                  </template>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <template v-if="balance.carriedForwardByName || balance.carriedForwardAt">
                    <div>{{ balance.carriedForwardByName || balance.carriedForwardBy || '-' }}</div>
                    <small class="text-muted">{{ formatDate(balance.carriedForwardAt) }}</small>
                  </template>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-center">
                  <span class="badge" :class="balance.status === 'Active' ? 'bg-success' : 'bg-secondary'">
                    {{ balance.status }}
                  </span>
                </td>
                <td class="text-center">
                  <button
                      class="btn btn-sm btn-outline-primary"
                      @click="openEditModal(balance)"
                      title="Edit Balance"
                  >
                    <i class="mdi mdi-pencil"></i>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- Balance audit trail -->
          <hr class="my-4">
          <h6 class="text-primary mb-3">
            <i class="mdi mdi-clipboard-text-outline me-1"></i>
            Balance Audit Trail
          </h6>
          <div v-if="!balanceEvents.length" class="text-center text-muted py-2">
            <i class="mdi mdi-information-outline"></i> No balance initialization or carry-forward events recorded yet
          </div>
          <div v-else class="table-responsive mb-4">
            <table class="table table-sm">
              <thead class="table-light">
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Leave Type</th>
                <th>Performed By</th>
                <th>Details</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(event, idx) in balanceEvents" :key="'evt-' + idx">
                <td>{{ formatDate(event.createdAt) }}</td>
                <td><span class="badge bg-info">{{ event.action }}</span></td>
                <td>{{ event.leaveTypeName || 'All' }}</td>
                <td>{{ event.performedByName || event.performedBy || '-' }}</td>
                <td><small class="text-muted">{{ event.details || '-' }}</small></td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- Leave History Section -->
          <hr class="my-4">
          <h6 class="text-primary mb-3">
            <i class="mdi mdi-history me-1"></i>
            Recent Leave History (approvals)
          </h6>

          <div v-if="leaveHistory.length === 0" class="text-center text-muted py-3">
            <i class="mdi mdi-information-outline"></i> No leave records for this year
          </div>

          <div v-else class="table-responsive">
            <table class="table table-sm">
              <thead class="table-light">
              <tr>
                <th>Leave Type</th>
                <th>Period</th>
                <th>Days</th>
                <th>Status</th>
                <th>Approved By</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="record in leaveHistory" :key="record.id">
                <td>{{ record.leaveTypeName }}</td>
                <td>{{ formatDate(record.startDate) }} - {{ formatDate(record.endDate) }}</td>
                <td>{{ record.totalDays }}</td>
                <td>
                  <span class="badge" :class="getLeaveStatusClass(record.status)">
                    {{ record.status }}
                  </span>
                </td>
                <td>{{ record.approvedBy || '-' }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="custom-modal-footer">
          <button
              type="button"
              class="btn btn-danger"
              @click="openRollbackModal"
              :disabled="!hasCarriedOverDays"
          >
            <i class="mdi mdi-undo me-1"></i>
            Rollback Carry Forward
          </button>
          <button
              type="button"
              class="btn btn-warning"
              @click="openSingleCashOutModal"
          >
            <i class="mdi mdi-cash me-1"></i>
            Cash Out Leave
          </button>
          <button
              type="button"
              class="btn btn-info"
              @click="openSingleCarryForward"
          >
            <i class="mdi mdi-transfer-right me-1"></i>
            Carry Forward
          </button>
          <button
              type="button"
              class="btn btn-success"
              @click="initializeFromModal"
              :disabled="isFullyInitialized"
          >
            <i class="mdi mdi-playlist-plus me-1"></i>
            Initialize Missing
          </button>
          <button type="button" class="btn btn-secondary" @click="closeDetailsModal">Close</button>
        </div>
      </div>
    </div>

    <!-- Edit Balance Modal (Custom Vue Modal) -->
    <div v-if="showEditModal" class="custom-modal-overlay" @click.self="closeEditModal">
      <div class="custom-modal">
        <div class="custom-modal-header bg-warning">
          <h5 class="custom-modal-title text-white">
            <i class="mdi mdi-pencil me-2"></i>
            Update Leave Balance
          </h5>
          <button type="button" class="custom-modal-close" @click="closeEditModal">×</button>
        </div>
        <div class="custom-modal-body">
          <div class="mb-3">
            <label class="form-label fw-medium">Employee</label>
            <p class="mb-0">{{ selectedEmployee.fullName || (selectedEmployee.firstName + ' ' + selectedEmployee.lastName) }}</p>
          </div>

          <div class="mb-3">
            <label class="form-label fw-medium">Leave Type</label>
            <p class="mb-0">{{ editForm.leaveTypeName }}</p>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Allocated Days <span class="text-danger">*</span></label>
              <input
                  type="number"
                  class="form-control"
                  v-model="editForm.totalDays"
                  min="0"
                  step="0.5"
                  required
              >
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Carried Over Days</label>
              <input
                  type="number"
                  class="form-control"
                  v-model="editForm.carriedOverDays"
                  min="0"
                  step="0.5"
              >
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Used Days <span class="text-danger">*</span></label>
              <input
                  type="number"
                  class="form-control"
                  v-model="editForm.usedDays"
                  min="0"
                  step="0.5"
                  required
              >
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Remaining Days</label>
              <input
                  type="number"
                  class="form-control"
                  :value="calculateRemaining"
                  disabled
              >
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Remarks</label>
            <textarea
                class="form-control"
                v-model="editForm.remarks"
                rows="2"
                placeholder="Reason for adjustment..."
            ></textarea>
          </div>

          <div class="alert alert-info small mb-0">
            <i class="mdi mdi-information me-1"></i>
            Use this to manually update leave balances.
          </div>
        </div>
        <div class="custom-modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeEditModal">Cancel</button>
          <button
              type="button"
              class="btn btn-primary"
              @click="saveBalance"
              :disabled="saving"
          >
            <span v-if="saving">
              <span class="spinner-border spinner-border-sm me-1"></span>
              Saving...
            </span>
            <span v-else>
              <i class="mdi mdi-content-save me-1"></i>
              Save Changes
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Quick Adjust Modal (Custom Vue Modal) -->
    <div v-if="showQuickAdjustModal" class="custom-modal-overlay" @click.self="closeQuickAdjustModal">
      <div class="custom-modal">
        <div class="custom-modal-header bg-info text-white">
          <h5 class="custom-modal-title text-white">
            <i class="mdi mdi-tune me-2"></i>
            Quick Balance Adjustment
          </h5>
          <button type="button" class="custom-modal-close" @click="closeQuickAdjustModal">×</button>
        </div>
        <div class="custom-modal-body">
          <div class="mb-3">
            <label class="form-label fw-medium">Employee</label>
            <p class="mb-0">{{ adjustForm.employeeName }}</p>
          </div>

          <div class="mb-3">
            <label class="form-label">Leave Type <span class="text-danger">*</span></label>
            <select class="form-select" v-model="adjustForm.leaveTypeId" required>
              <option value="">Select Leave Type</option>
              <option v-for="lt in leaveTypes" :key="lt.id" :value="lt.id">
                {{ lt.typeName }}
              </option>
            </select>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Adjustment Type <span class="text-danger">*</span></label>
              <select class="form-select" v-model="adjustForm.adjustmentType">
                <option value="deduct">Deduct Days (Leave Taken)</option>
                <option value="add">Add Days (Credit Back)</option>
              </select>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Days <span class="text-danger">*</span></label>
              <input
                  type="number"
                  class="form-control"
                  v-model="adjustForm.days"
                  min="0.5"
                  step="0.5"
                  required
              >
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Reason <span class="text-danger">*</span></label>
            <textarea
                class="form-control"
                v-model="adjustForm.reason"
                rows="2"
                placeholder="e.g., Leave taken 10-15 Nov without system application"
                required
            ></textarea>
          </div>
        </div>
        <div class="custom-modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeQuickAdjustModal">Cancel</button>
          <button
              type="button"
              class="btn btn-primary"
              @click="submitAdjustment"
              :disabled="adjusting"
          >
            <span v-if="adjusting">
              <span class="spinner-border spinner-border-sm me-1"></span>
              Processing...
            </span>
            <span v-else>
              <i class="mdi mdi-check me-1"></i>
              Apply Adjustment
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Bulk Initialize Modal -->
    <div v-if="showBulkInitModal" class="custom-modal-overlay" @click.self="closeBulkInitModal">
      <div class="custom-modal">
        <div class="custom-modal-header bg-success text-white">
          <h5 class="custom-modal-title text-white">
            <i class="mdi mdi-playlist-plus me-2"></i>
            Bulk Initialize Leave Balances
          </h5>
          <button type="button" class="custom-modal-close" @click="closeBulkInitModal">×</button>
        </div>
        <div class="custom-modal-body">
          <p>You are about to initialize leave balances for <strong>{{ selectedEmployees.length }}</strong> employee(s) for the year <strong>{{ selectedYear }}</strong>.</p>

          <div class="alert alert-warning">
            <i class="mdi mdi-alert me-1"></i>
            This will create default leave balances based on active leave types. Existing balances will not be overwritten.
          </div>

          <p class="mb-0">Do you want to proceed?</p>
        </div>
        <div class="custom-modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeBulkInitModal">Cancel</button>
          <button
              type="button"
              class="btn btn-success"
              @click="executeBulkInitialize"
              :disabled="bulkInitializing"
          >
            <span v-if="bulkInitializing">
              <span class="spinner-border spinner-border-sm me-1"></span>
              Initializing...
            </span>
            <span v-else>
              <i class="mdi mdi-check-all me-1"></i>
              Yes, Initialize
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Carry Forward Modal (overlay click + Escape key close for nginx compatibility) -->
    <div
      v-if="showCarryForwardModal"
      ref="carryForwardOverlay"
      class="custom-modal-overlay carry-forward-overlay"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      @click="onCarryForwardOverlayClick"
      @keydown.esc.stop="closeCarryForwardModal"
    >
      <div class="custom-modal custom-modal-lg" @click.stop>
        <div class="custom-modal-header bg-info text-white">
          <h5 class="custom-modal-title text-white">
            <i class="mdi mdi-transfer-right me-2"></i>
            Carry Forward Leave Balances
          </h5>
          <button type="button" class="custom-modal-close" @click.stop.prevent="closeCarryForwardModal" aria-label="Close">×</button>
        </div>
        <div class="custom-modal-body">
          <div class="row mb-3">
            <div class="col-md-4">
              <label class="form-label">From Year <span class="text-danger">*</span></label>
              <select class="form-select" v-model="carryForwardForm.fromYear" @change="loadCarryForwardPreview">
                <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">To Year <span class="text-danger">*</span></label>
              <select class="form-select" v-model="carryForwardForm.toYear">
                <option v-for="year in toYearOptions" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">Max Days to Carry</label>
              <input
                  type="number"
                  class="form-control"
                  v-model="carryForwardForm.maxDays"
                  min="1"
                  max="30"
                  @change="loadCarryForwardPreview"
              >
            </div>
          </div>

          <div class="alert alert-info">
            <i class="mdi mdi-information me-1"></i>
            <strong>{{ selectedEmployees.length }}</strong> employee(s) selected.
            Remaining days from <strong>{{ carryForwardForm.fromYear }}</strong> will be carried forward to <strong>{{ carryForwardForm.toYear }}</strong> (max {{ carryForwardForm.maxDays }} days per leave type).
          </div>

          <!-- Preview for single employee -->
          <div v-if="selectedEmployees.length === 1 && carryForwardPreview.length > 0">
            <h6 class="text-primary mb-2">
              <i class="mdi mdi-eye me-1"></i>
              Preview
            </h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                <tr>
                  <th>Leave Type</th>
                  <th class="text-center">Remaining ({{ carryForwardForm.fromYear }})</th>
                  <th class="text-center">Eligible to Carry</th>
                  <th class="text-center">Action</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="item in carryForwardPreview" :key="item.leaveTypeID">
                  <td>{{ item.leaveTypeName }}</td>
                  <td class="text-center">{{ item.remainingDays }}</td>
                  <td class="text-center">
                    <span class="badge bg-success">{{ item.eligibleCarryForward }} days</span>
                  </td>
                  <td class="text-center">
                      <span class="badge" :class="item.action === 'Will Create' ? 'bg-primary' : 'bg-warning'">
                        {{ item.action }}
                      </span>
                  </td>
                </tr>
                </tbody>
                <tfoot class="table-light">
                <tr>
                  <th>Total</th>
                  <th class="text-center">-</th>
                  <th class="text-center">{{ totalEligibleDays }} days</th>
                  <th></th>
                </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div v-if="previewLoading" class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <span class="ms-2">Loading preview...</span>
          </div>
        </div>
        <div class="custom-modal-footer">
          <button type="button" class="btn btn-secondary" @click.stop="closeCarryForwardModal">Cancel</button>
          <button
              type="button"
              class="btn btn-info"
              @click="executeCarryForward"
              :disabled="carryingForward || carryForwardForm.fromYear >= carryForwardForm.toYear"
          >
            <span v-if="carryingForward">
              <span class="spinner-border spinner-border-sm me-1"></span>
              Processing...
            </span>
            <span v-else>
              <i class="mdi mdi-transfer-right me-1"></i>
              Carry Forward
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Single Employee Carry Forward Modal -->
    <div v-if="showSingleCarryForwardModal" class="custom-modal-overlay" @click.self="closeSingleCarryForwardModal">
      <div class="custom-modal custom-modal-lg">
        <div class="custom-modal-header bg-info text-white">
          <h5 class="custom-modal-title text-white">
            <i class="mdi mdi-transfer-right me-2"></i>
            Carry Forward - {{ selectedEmployee.fullName || (selectedEmployee.firstName + ' ' + selectedEmployee.lastName) }}
          </h5>
          <button type="button" class="custom-modal-close" @click="closeSingleCarryForwardModal">×</button>
        </div>
        <div class="custom-modal-body">
          <div class="row mb-3">
            <div class="col-md-4">
              <label class="form-label">From Year <span class="text-danger">*</span></label>
              <select class="form-select" v-model="singleCarryForm.fromYear" @change="loadSingleCarryPreview">
                <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">To Year <span class="text-danger">*</span></label>
              <select class="form-select" v-model="singleCarryForm.toYear">
                <option v-for="year in singleToYearOptions" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">Max Days to Carry</label>
              <input
                  type="number"
                  class="form-control"
                  v-model="singleCarryForm.maxDays"
                  min="1"
                  max="30"
                  @change="loadSingleCarryPreview"
              >
            </div>
          </div>

          <!-- Preview Table -->
          <div v-if="singleCarryPreview.length > 0">
            <h6 class="text-primary mb-2">
              <i class="mdi mdi-eye me-1"></i>
              Carry Forward Preview
            </h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                <tr>
                  <th>Leave Type</th>
                  <th class="text-center">Total ({{ singleCarryForm.fromYear }})</th>
                  <th class="text-center">Used</th>
                  <th class="text-center">Remaining</th>
                  <th class="text-center">Eligible to Carry</th>
                  <th class="text-center">Action</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="item in singleCarryPreview" :key="item.leaveTypeID">
                  <td>{{ item.leaveTypeName }}</td>
                  <td class="text-center">{{ item.totalDays }}</td>
                  <td class="text-center">{{ item.usedDays }}</td>
                  <td class="text-center">{{ item.remainingDays }}</td>
                  <td class="text-center">
                      <span class="badge" :class="item.eligibleCarryForward > 0 ? 'bg-success' : 'bg-secondary'">
                        {{ item.eligibleCarryForward }} days
                      </span>
                  </td>
                  <td class="text-center">
                      <span class="badge" :class="item.action === 'Will Create' ? 'bg-primary' : 'bg-warning'">
                        {{ item.action }}
                      </span>
                  </td>
                </tr>
                </tbody>
                <tfoot class="table-light">
                <tr>
                  <th colspan="4">Total Eligible to Carry Forward</th>
                  <th class="text-center">{{ singleTotalEligible }} days</th>
                  <th></th>
                </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div v-else-if="!singlePreviewLoading" class="alert alert-warning">
            <i class="mdi mdi-alert me-1"></i>
            No leave balances found for {{ singleCarryForm.fromYear }}. Please select a different year.
          </div>

          <div v-if="singlePreviewLoading" class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <span class="ms-2">Loading preview...</span>
          </div>
        </div>
        <div class="custom-modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeSingleCarryForwardModal">Cancel</button>
          <button
              type="button"
              class="btn btn-info"
              @click="executeSingleCarryForward"
              :disabled="singleCarryingForward || singleCarryForm.fromYear >= singleCarryForm.toYear || singleTotalEligible === 0"
          >
            <span v-if="singleCarryingForward">
              <span class="spinner-border spinner-border-sm me-1"></span>
              Processing...
            </span>
            <span v-else>
              <i class="mdi mdi-transfer-right me-1"></i>
              Carry Forward
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Rollback Modal -->
    <div v-if="showRollbackModal" class="custom-modal-overlay" @click.self="closeRollbackModal">
      <div class="custom-modal custom-modal-lg">
        <div class="custom-modal-header bg-danger">
          <h5 class="custom-modal-title text-white">
            <i class="mdi mdi-undo me-2"></i>
            Rollback Carry Forward - {{ selectedEmployee.fullName || (selectedEmployee.firstName + ' ' + selectedEmployee.lastName) }}
          </h5>
          <button type="button" class="custom-modal-close" @click="closeRollbackModal">×</button>
        </div>
        <div class="custom-modal-body">
          <div class="alert alert-danger">
            <i class="mdi mdi-alert me-1"></i>
            <strong>Warning:</strong> This will remove the carried forward days from the selected year.
            If the employee has already used some of the carried forward days, the rollback may be partial.
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <label class="form-label">Year to Rollback <span class="text-danger">*</span></label>
              <select class="form-select" v-model="rollbackForm.year" @change="loadRollbackPreview">
                <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label">Reason <span class="text-danger">*</span></label>
              <input type="text" class="form-control" v-model="rollbackForm.reason" placeholder="Reason for rollback">
            </div>
          </div>

          <!-- Rollback Preview -->
          <div v-if="rollbackPreview.length > 0">
            <h6 class="text-danger mb-2">
              <i class="mdi mdi-eye me-1"></i>
              Rollback Preview
            </h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                <tr>
                  <th>Leave Type</th>
                  <th class="text-center">Carried Over Days</th>
                  <th class="text-center">Current Remaining</th>
                  <th class="text-center">After Rollback</th>
                  <th class="text-center">Status</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="item in rollbackPreview" :key="item.id">
                  <td>{{ item.leaveTypeName }}</td>
                  <td class="text-center">
                    <span class="badge bg-info">{{ item.carriedOverDays }} days</span>
                  </td>
                  <td class="text-center">{{ item.remainingDays }}</td>
                  <td class="text-center">{{ item.newRemainingAfterRollback }}</td>
                  <td class="text-center">
                      <span class="badge" :class="item.rollbackStatus.includes('Partial') ? 'bg-warning' : 'bg-success'">
                        {{ item.rollbackStatus }}
                      </span>
                  </td>
                </tr>
                </tbody>
                <tfoot class="table-light">
                <tr>
                  <th>Total to Remove</th>
                  <th class="text-center">{{ totalCarriedOverToRollback }} days</th>
                  <th colspan="3"></th>
                </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div v-else-if="!rollbackPreviewLoading" class="alert alert-info">
            <i class="mdi mdi-information me-1"></i>
            No carried forward balances found for {{ rollbackForm.year }}.
          </div>

          <div v-if="rollbackPreviewLoading" class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <span class="ms-2">Loading preview...</span>
          </div>
        </div>
        <div class="custom-modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeRollbackModal">Cancel</button>
          <button
              type="button"
              class="btn btn-danger"
              @click="executeRollback"
              :disabled="rollingBack || rollbackPreview.length === 0 || !rollbackForm.reason"
          >
            <span v-if="rollingBack">
              <span class="spinner-border spinner-border-sm me-1"></span>
              Rolling Back...
            </span>
            <span v-else>
              <i class="mdi mdi-undo me-1"></i>
              Confirm Rollback
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Single Employee Cash Out Modal -->
    <div v-if="showSingleCashoutModal" class="custom-modal-overlay" @click.self="closeSingleCashoutModal">
      <div class="custom-modal custom-modal-lg">
        <div class="custom-modal-header bg-warning">
          <h5 class="custom-modal-title text-white">
            <i class="mdi mdi-cash me-2"></i>
            Cash Out Leave - {{ selectedEmployee.fullName || (selectedEmployee.firstName + ' ' + selectedEmployee.lastName) }}
          </h5>
          <button type="button" class="custom-modal-close" @click="closeSingleCashoutModal">×</button>
        </div>
        <div class="custom-modal-body">
          <div class="alert alert-info">
            <i class="mdi mdi-information me-1"></i>
            Cash out allows employees to receive payment for unused leave days instead of carrying them forward.
          </div>

          <div class="row mb-3">
            <div class="col-md-4">
              <label class="form-label">Year <span class="text-danger">*</span></label>
              <select class="form-select" v-model="cashOutForm.year" @change="loadCashOutPreview">
                <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">Leave Type <span class="text-danger">*</span></label>
              <select class="form-select" v-model="cashOutForm.leaveTypeId" @change="onLeaveTypeChange">
                <option value="">Select Leave Type</option>
                <option v-for="bal in cashOutBalances" :key="bal.leaveTypeID" :value="bal.leaveTypeID">
                  {{ bal.leaveTypeName }} ({{ bal.remainingDays }} days available)
                </option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">Days to Cash Out <span class="text-danger">*</span></label>
              <input
                  type="number"
                  class="form-control"
                  v-model="cashOutForm.days"
                  min="0.5"
                  step="0.5"
                  :max="cashOutForm.maxDays"
              >
              <small class="text-muted" v-if="cashOutForm.maxDays">Max: {{ cashOutForm.maxDays }} days</small>
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-4">
              <label class="form-label">Amount Per Day (KES)</label>
              <input
                  type="number"
                  class="form-control"
                  v-model="cashOutForm.amountPerDay"
                  min="0"
                  step="0.01"
              >
              <small class="text-muted" v-if="suggestedDailyRate">Suggested: {{ formatCurrency(suggestedDailyRate) }}</small>
            </div>
            <div class="col-md-4">
              <label class="form-label">Total Amount</label>
              <input
                  type="text"
                  class="form-control bg-light"
                  :value="formatCurrency(calculatedTotalAmount)"
                  disabled
              >
            </div>
            <div class="col-md-4">
              <label class="form-label">Payment Reference</label>
              <input
                  type="text"
                  class="form-control"
                  v-model="cashOutForm.paymentReference"
                  placeholder="e.g., PAY-2024-001"
              >
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Remarks</label>
            <textarea
                class="form-control"
                v-model="cashOutForm.remarks"
                rows="2"
                placeholder="Any additional notes..."
            ></textarea>
          </div>

          <!-- Available Balances Preview -->
          <div v-if="cashOutBalances.length > 0" class="mt-3">
            <h6 class="text-primary mb-2">Available Balances for {{ cashOutForm.year }}</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                <tr>
                  <th>Leave Type</th>
                  <th class="text-center">Total</th>
                  <th class="text-center">Used</th>
                  <th class="text-center">Available</th>
                  <th class="text-center">Paid Leave?</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="bal in cashOutBalances" :key="bal.leaveTypeID"
                    :class="{'table-primary': bal.leaveTypeID === cashOutForm.leaveTypeId}">
                  <td>{{ bal.leaveTypeName }}</td>
                  <td class="text-center">{{ bal.totalDays }}</td>
                  <td class="text-center">{{ bal.usedDays }}</td>
                  <td class="text-center">
                    <span class="badge bg-success">{{ bal.remainingDays }} days</span>
                  </td>
                  <td class="text-center">
                      <span class="badge" :class="bal.isPaid === '1' ? 'bg-success' : 'bg-secondary'">
                        {{ bal.isPaid === '1' ? 'Yes' : 'No' }}
                      </span>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="cashOutPreviewLoading" class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <span class="ms-2">Loading...</span>
          </div>
        </div>
        <div class="custom-modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeSingleCashoutModal">Cancel</button>
          <button
              type="button"
              class="btn btn-warning"
              @click="executeCashOut"
              :disabled="cashingOut || !cashOutForm.leaveTypeId || !cashOutForm.days || cashOutForm.days <= 0"
          >
            <span v-if="cashingOut">
              <span class="spinner-border spinner-border-sm me-1"></span>
              Processing...
            </span>
            <span v-else>
              <i class="mdi mdi-cash me-1"></i>
              Process Cash Out
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Bulk Cash Out Modal -->
    <div v-if="showBulkCashoutModal" class="custom-modal-overlay" @click.self="closeBulkCashoutModal">
      <div class="custom-modal">
        <div class="custom-modal-header bg-warning">
          <h5 class="custom-modal-title text-white">
            <i class="mdi mdi-cash-multiple me-2"></i>
            Bulk Cash Out Leave
          </h5>
          <button type="button" class="custom-modal-close" @click="closeBulkCashoutModal">×</button>
        </div>
        <div class="custom-modal-body">
          <p><strong>{{ selectedEmployees.length }}</strong> employee(s) selected for cash out.</p>

          <div class="mb-3">
            <label class="form-label">Year <span class="text-danger">*</span></label>
            <select class="form-select" v-model="bulkCashOutForm.year">
              <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label">Leave Type <span class="text-danger">*</span></label>
            <select class="form-select" v-model="bulkCashOutForm.leaveTypeId">
              <option value="">Select Leave Type</option>
              <option v-for="lt in leaveTypes" :key="lt.id" :value="lt.id">
                {{ lt.typeName }}
              </option>
            </select>
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <label class="form-label">Max Days Per Employee</label>
              <input
                  type="number"
                  class="form-control"
                  v-model="bulkCashOutForm.maxDays"
                  min="0.5"
                  step="0.5"
                  placeholder="Leave empty for all"
              >
            </div>
            <div class="col-md-6">
              <label class="form-label">Amount Per Day (KES)</label>
              <input
                  type="number"
                  class="form-control"
                  v-model="bulkCashOutForm.amountPerDay"
                  min="0"
                  step="0.01"
              >
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Payment Reference</label>
            <input
                type="text"
                class="form-control"
                v-model="bulkCashOutForm.paymentReference"
                placeholder="e.g., BULK-PAY-2024-001"
            >
          </div>

          <div class="mb-3">
            <label class="form-label">Remarks</label>
            <textarea
                class="form-control"
                v-model="bulkCashOutForm.remarks"
                rows="2"
            ></textarea>
          </div>

          <div class="alert alert-warning">
            <i class="mdi mdi-alert me-1"></i>
            This will cash out available days for all selected employees. Employees without balances will be skipped.
          </div>
        </div>
        <div class="custom-modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeBulkCashoutModal">Cancel</button>
          <button
              type="button"
              class="btn btn-warning"
              @click="executeBulkCashOut"
              :disabled="bulkCashingOut || !bulkCashOutForm.leaveTypeId"
          >
            <span v-if="bulkCashingOut">
              <span class="spinner-border spinner-border-sm me-1"></span>
              Processing...
            </span>
            <span v-else>
              <i class="mdi mdi-cash-multiple me-1"></i>
              Process Bulk Cash Out
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Download Progress Modal -->
    <div v-if="showDownloadProgressModal" class="custom-modal-overlay" @click.self="closeDownloadProgressModal">
      <div class="custom-modal custom-modal-centered">
        <div class="custom-modal-header bg-primary text-white">
          <h5 class="custom-modal-title text-white">
            <i class="mdi mdi-download me-2"></i>
            Downloading Report
          </h5>
        </div>
        <div class="custom-modal-body text-center py-4">
          <div class="mb-4">
            <div class="download-icon-wrapper mb-3">
              <i class="mdi mdi-file-download-outline text-primary" style="font-size: 48px;"></i>
            </div>
            <h5 class="mb-1">{{ downloadProgress.status }}</h5>
            <p class="text-muted mb-0">{{ downloadProgress.message }}</p>
          </div>

          <!-- Progress Bar -->
          <div class="progress mb-3" style="height: 20px;">
            <div
                class="progress-bar progress-bar-striped progress-bar-animated"
                :class="downloadProgress.percent === 100 ? 'bg-success' : 'bg-primary'"
                role="progressbar"
                :style="{ width: downloadProgress.percent + '%' }"
                :aria-valuenow="downloadProgress.percent"
                aria-valuemin="0"
                aria-valuemax="100">
              {{ downloadProgress.percent }}%
            </div>
          </div>

          <!-- Stats -->
          <div class="row text-center">
            <div class="col-4">
              <div class="border-end">
                <h5 class="mb-1 text-primary">{{ downloadProgress.fetched }}</h5>
                <small class="text-muted">Records Fetched</small>
              </div>
            </div>
            <div class="col-4">
              <div class="border-end">
                <h5 class="mb-1 text-info">{{ downloadProgress.totalRecords }}</h5>
                <small class="text-muted">Total Records</small>
              </div>
            </div>
            <div class="col-4">
              <div>
                <h5 class="mb-1 text-warning">{{ downloadProgress.timeRemaining }}</h5>
                <small class="text-muted">Time Remaining</small>
              </div>
            </div>
          </div>

          <!-- Cancel Button -->
          <div class="mt-4" v-if="downloadProgress.percent < 100">
            <button
                type="button"
                class="btn btn-outline-danger"
                @click="cancelDownload"
            >
              <i class="mdi mdi-close me-1"></i>
              Cancel Download
            </button>
          </div>

          <!-- Done Button -->
          <div class="mt-4" v-if="downloadProgress.percent === 100">
            <button
                type="button"
                class="btn btn-success"
                @click="closeDownloadProgressModal"
            >
              <i class="mdi mdi-check me-1"></i>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Success/Error Modals -->
    <SuccessModal
        ref="successModal"
        title="Operation Successful"
        heading="Success!"
        :message="successMessage"
    />
    <ErrorModal
        ref="errorModal"
        title="Operation Failed"
        heading="Error"
        :message="errorMessage"
    />
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import SuccessModal from "./modals/SuccessModal.vue";
import ErrorModal from "./modals/ErrorModal.vue";
import { authUrl, execute, executeDownload } from "@/api";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "LeaveBalancesManagement",
  components: {
    HrPageLayout,
    SuccessModal,
    ErrorModal
  },
  data() {
    return {
      // Loading states
      loading: false,
      detailsLoading: false,
      downloadLoading: false,
      saving: false,
      adjusting: false,
      bulkInitializing: false,

      // Messages
      successMessage: '',
      errorMessage: '',

      // Filters
      searchValue: '',
      selectedYear: new Date().getFullYear(),
      selectedDepartment: '',
      initStatusFilter: '',

      // Data
      employees: [],
      departments: [],
      leaveTypes: [],
      availableYears: [],
      selectedEmployees: [],

      // Pagination
      currentPage: 1,
      totalPages: 0,
      pageSize: 10,
      totalItems: 0,

      // Metrics
      metrics: {
        totalEmployees: 0,
        fullyInitialized: 0,
        notInitialized: 0,
        partiallyInitialized: 0
      },

      // Stats configuration
      statsCards: [
        { key: 'totalEmployees', label: 'Total Active Employees', icon: 'bx-group' },
        { key: 'fullyInitialized', label: 'Fully Initialized', icon: 'bx-check-circle' },
        { key: 'partiallyInitialized', label: 'Partially Initialized', icon: 'bx-error-circle' },
        { key: 'notInitialized', label: 'Not Initialized', icon: 'bx-x-circle' }
      ],

      // Selected employee details
      selectedEmployee: {},
      employeeBalances: [],
      leaveHistory: [],
      balanceEvents: [],

      // Modal visibility flags
      showDetailsModal: false,
      showEditModal: false,
      showQuickAdjustModal: false,
      showBulkInitModal: false,
      showCarryForwardModal: false,
      showSingleCarryForwardModal: false,
      showRollbackModal: false,
      showSingleCashoutModal: false,
      showBulkCashoutModal: false,
      showDownloadProgressModal: false,

      // Edit form
      editForm: {
        leaveTypeId: '',
        leaveTypeName: '',
        totalDays: 0,
        usedDays: 0,
        carriedOverDays: 0,
        remarks: ''
      },

      // Quick adjust form
      adjustForm: {
        nationalId: '',
        employeeName: '',
        leaveTypeId: '',
        adjustmentType: 'deduct',
        days: 1,
        reason: ''
      },

      // Carry forward form (bulk)
      carryForwardForm: {
        fromYear: new Date().getFullYear() - 1,
        toYear: new Date().getFullYear(),
        maxDays: 5
      },
      carryForwardPreview: [],
      totalEligibleDays: 0,
      previewLoading: false,
      carryingForward: false,

      // Single employee carry forward
      singleCarryForm: {
        fromYear: new Date().getFullYear() - 1,
        toYear: new Date().getFullYear(),
        maxDays: 5
      },
      singleCarryPreview: [],
      singleTotalEligible: 0,
      singlePreviewLoading: false,
      singleCarryingForward: false,

      // Rollback form
      rollbackForm: {
        year: new Date().getFullYear(),
        reason: ''
      },
      rollbackPreview: [],
      totalCarriedOverToRollback: 0,
      rollbackPreviewLoading: false,
      rollingBack: false,

      // Single cash out form
      cashOutForm: {
        year: new Date().getFullYear(),
        leaveTypeId: '',
        days: 0,
        maxDays: 0,
        amountPerDay: 0,
        paymentReference: '',
        remarks: ''
      },
      cashOutBalances: [],
      suggestedDailyRate: 0,
      cashOutPreviewLoading: false,
      cashingOut: false,

      // Bulk cash out form
      bulkCashOutForm: {
        year: new Date().getFullYear(),
        leaveTypeId: '',
        maxDays: '',
        amountPerDay: 0,
        paymentReference: '',
        remarks: ''
      },
      bulkCashingOut: false,

      // Download progress
      downloadProgress: {
        status: 'Preparing...',
        message: 'Initializing download...',
        percent: 0,
        fetched: 0,
        totalRecords: 0,
        timeRemaining: '--:--',
        cancelled: false,
        startTime: null
      },
      allDownloadData: []
    };
  },
  computed: {
    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },
    endIndex() {
      return this.currentPage * this.pageSize;
    },
    isAllSelected() {
      return this.employees.length > 0 &&
          this.selectedEmployees.length === this.employees.length;
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < this.totalPages) {
        if (end < this.totalPages - 1) pages.push('...');
        pages.push(this.totalPages);
      }

      return pages;
    },
    calculateRemaining() {
      const total = parseFloat(this.editForm.totalDays) || 0;
      const carried = parseFloat(this.editForm.carriedOverDays) || 0;
      const used = parseFloat(this.editForm.usedDays) || 0;
      return (total + carried - used).toFixed(1);
    },
    isFullyInitialized() {
      return this.employeeBalances.every(b => b.status === 'Active');
    },
    toYearOptions() {
      return this.availableYears.filter(y => y > this.carryForwardForm.fromYear);
    },
    singleToYearOptions() {
      return this.availableYears.filter(y => y > this.singleCarryForm.fromYear);
    },
    hasCarriedOverDays() {
      return this.employeeBalances.some(b => parseFloat(b.carriedOverDays) > 0);
    },
    calculatedTotalAmount() {
      return (parseFloat(this.cashOutForm.days) || 0) * (parseFloat(this.cashOutForm.amountPerDay) || 0);
    }
  },
  mounted() {
    // Ensure Carry Forward modal is never open on load (e.g. live server / rehydration)
    this.showCarryForwardModal = false;
    this.removeCarryForwardEscapeListener();
    this.initializeData();
    // Add body class when any modal is open
    this.$watch('showDetailsModal', this.handleBodyScroll);
    this.$watch('showEditModal', this.handleBodyScroll);
    this.$watch('showQuickAdjustModal', this.handleBodyScroll);
    this.$watch('showBulkInitModal', this.handleBodyScroll);
    this.$watch('showCarryForwardModal', this.handleBodyScroll);
    this.$watch('showSingleCarryForwardModal', this.handleBodyScroll);
    this.$watch('showRollbackModal', this.handleBodyScroll);
    this.$watch('showSingleCashoutModal', this.handleBodyScroll);
    this.$watch('showBulkCashoutModal', this.handleBodyScroll);
    this.$watch('showDownloadProgressModal', this.handleBodyScroll);
  },
  beforeDestroy() {
    this.removeCarryForwardEscapeListener();
  },
  methods: {
    handleBodyScroll() {
      const anyModalOpen = 
        this.showDetailsModal ||
        this.showEditModal ||
        this.showQuickAdjustModal ||
        this.showBulkInitModal ||
        this.showCarryForwardModal ||
        this.showSingleCarryForwardModal ||
        this.showRollbackModal ||
        this.showSingleCashoutModal ||
        this.showBulkCashoutModal ||
        this.showDownloadProgressModal;

      if (anyModalOpen) {
        document.body.classList.add('modal-open');
        document.body.style.overflow = 'hidden';
      } else {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
      }
    },

    async initializeData() {
      await Promise.all([
        this.fetchYears(),
        this.fetchDepartments(),
        this.fetchLeaveTypes()
      ]);
      await this.fetchEmployees();
    },

    // Fetch available years
    async fetchYears() {
      const data = new FormData();
      data.append("function", "getAvailableYears");

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.availableYears = res.data.data.years;
        }
      } catch (error) {
        console.error('Error fetching years:', error);
      }
    },

    // Fetch departments
    async fetchDepartments() {
      const data = new FormData();
      data.append("function", "getDepartmentsForFilter");

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.departments = res.data.data.departments;
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    },

    // Fetch leave types (for bulk cash out dropdown and elsewhere)
    async fetchLeaveTypes() {
      const data = new FormData();
      data.append("function", "getActiveLeaveTypes");

      try {
        const res = await execute(data, authUrl);
        const payload = res?.data?.data || res?.data || {};
        this.leaveTypes = Array.isArray(payload.leaveTypes) ? payload.leaveTypes : [];
      } catch (error) {
        console.error('Error fetching leave types:', error);
        this.leaveTypes = [];
      }
    },

    // Fetch employees with leave balance status
    async fetchEmployees() {
      this.loading = true;
      this.selectedEmployees = [];

      const data = new FormData();
      data.append("function", "getLeaveBalancesManagement");
      data.append("page", this.currentPage);
      data.append("limit", this.pageSize);
      data.append("year", this.selectedYear);
      data.append("search", this.searchValue);
      data.append("departmentId", this.selectedDepartment);
      data.append("initStatus", this.initStatusFilter);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.employees = res.data.data.employees.map(emp => ({
            ...emp,
            processing: false
          }));
          this.metrics = res.data.data.metrics;
          this.totalItems = res.data.data.totalCount;
          this.totalPages = Math.ceil(this.totalItems / this.pageSize);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
        this.showError('Failed to load employee data');
      } finally {
        this.loading = false;
      }
    },

    // View employee details
    async viewEmployeeDetails(emp) {
      this.selectedEmployee = emp;
      this.detailsLoading = true;
      this.showDetailsModal = true;

      const data = new FormData();
      data.append("function", "getEmployeeLeaveBalanceDetails");
      data.append("nationalId", emp.nationalId);
      data.append("year", this.selectedYear);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.employeeBalances = res.data.data.leaveBalances || [];
          this.leaveHistory = res.data.data.leaveHistory || [];
          this.balanceEvents = res.data.data.balanceEvents || [];
        }
      } catch (error) {
        console.error('Error fetching employee details:', error);
        this.showError('Failed to load employee details');
      } finally {
        this.detailsLoading = false;
      }
    },

    closeDetailsModal() {
      this.showDetailsModal = false;
    },

    // Initialize single employee
    async initializeSingle(emp) {
      emp.processing = true;

      const data = new FormData();
      data.append("function", "initializeEmployeeLeaveBalances");
      data.append("nationalId", emp.nationalId);
      data.append("year", this.selectedYear);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.showSuccess(res.data.message);
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error initializing:', error);
        this.showError('Failed to initialize leave balances');
      } finally {
        emp.processing = false;
      }
    },

    // Initialize from details modal
    async initializeFromModal() {
      await this.initializeSingle(this.selectedEmployee);
      await this.viewEmployeeDetails(this.selectedEmployee);
    },

    // Open edit modal
    openEditModal(balance) {
      this.editForm = {
        leaveTypeId: balance.leaveTypeId,
        leaveTypeName: balance.typeName,
        totalDays: balance.totalDays || balance.defaultAllocation || 0,
        usedDays: balance.usedDays || 0,
        carriedOverDays: balance.carriedOverDays || 0,
        remarks: ''
      };
      
      // Close details modal and open edit modal
      this.showDetailsModal = false;
      setTimeout(() => {
        this.showEditModal = true;
      }, 50);
    },

    closeEditModal() {
      this.showEditModal = false;
      // Reopen details modal after edit closes
      setTimeout(() => {
        this.viewEmployeeDetails(this.selectedEmployee);
      }, 50);
    },

    // Save balance
    async saveBalance() {
      if (!this.editForm.totalDays && this.editForm.totalDays !== 0) {
        this.showError('Please enter allocated days');
        return;
      }

      this.saving = true;

      const data = new FormData();
      data.append("function", "updateLeaveBalance");
      data.append("nationalId", this.selectedEmployee.nationalId);
      data.append("leaveTypeId", this.editForm.leaveTypeId);
      data.append("year", this.selectedYear);
      data.append("totalDays", this.editForm.totalDays);
      data.append("usedDays", this.editForm.usedDays);
      data.append("carriedOverDays", this.editForm.carriedOverDays);
      data.append("remarks", this.editForm.remarks);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.showEditModal = false;
          this.showSuccess(res.data.message);
          await this.viewEmployeeDetails(this.selectedEmployee);
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error saving balance:', error);
        this.showError('Failed to save leave balance');
      } finally {
        this.saving = false;
      }
    },

    // Open quick adjust modal
    openAdjustModal(emp) {
      this.adjustForm = {
        nationalId: emp.nationalId,
        employeeName: emp.fullName,
        leaveTypeId: '',
        adjustmentType: 'deduct',
        days: 1,
        reason: ''
      };
      this.showQuickAdjustModal = true;
    },

    closeQuickAdjustModal() {
      this.showQuickAdjustModal = false;
    },

    // Submit quick adjustment
    async submitAdjustment() {
      if (!this.adjustForm.leaveTypeId || !this.adjustForm.days || !this.adjustForm.reason) {
        this.showError('Please fill all required fields');
        return;
      }

      this.adjusting = true;

      const data = new FormData();
      data.append("function", "adjustLeaveBalance");
      data.append("nationalId", this.adjustForm.nationalId);
      data.append("leaveTypeId", this.adjustForm.leaveTypeId);
      data.append("year", this.selectedYear);
      data.append("adjustmentType", this.adjustForm.adjustmentType);
      data.append("days", this.adjustForm.days);
      data.append("reason", this.adjustForm.reason);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.showQuickAdjustModal = false;
          this.showSuccess(res.data.message);
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error adjusting balance:', error);
        this.showError('Failed to adjust leave balance');
      } finally {
        this.adjusting = false;
      }
    },

    // Show bulk initialize modal
    showBulkInitializeModal() {
      if (this.selectedEmployees.length === 0) {
        this.showError('Please select at least one employee');
        return;
      }
      this.showBulkInitModal = true;
    },

    closeBulkInitModal() {
      this.showBulkInitModal = false;
    },

    // Execute bulk initialize
    async executeBulkInitialize() {
      this.bulkInitializing = true;

      const data = new FormData();
      data.append("function", "bulkInitializeLeaveBalances");
      data.append("nationalIds", JSON.stringify(this.selectedEmployees));
      data.append("year", String(this.selectedYear || new Date().getFullYear()));

      try {
        const res = await execute(data, authUrl);
        const success = res?.data?.success === true;
        const message = res?.data?.message || (success ? 'Bulk initialization complete.' : 'Bulk initialize failed.');
        if (success) {
          this.showBulkInitModal = false;
          this.showSuccess(message);
          this.selectedEmployees = [];
          await this.fetchEmployees();
        } else {
          this.showError(message);
        }
      } catch (error) {
        console.error('Error bulk initializing:', error);
        this.showError(error?.response?.data?.message || 'Failed to bulk initialize');
      } finally {
        this.bulkInitializing = false;
      }
    },

    // Download report with progress
    async downloadReport() {
      // Reset progress
      this.downloadProgress = {
        status: 'Preparing...',
        message: 'Calculating total records...',
        percent: 0,
        fetched: 0,
        totalRecords: 0,
        timeRemaining: '--:--',
        cancelled: false,
        startTime: Date.now()
      };
      this.allDownloadData = [];

      // Show progress modal
      this.showDownloadProgressModal = true;

      try {
        // First, get total count
        const countData = new FormData();
        countData.append("function", "getLeaveBalancesManagement");
        countData.append("page", 1);
        countData.append("limit", 1);
        countData.append("year", this.selectedYear);
        countData.append("search", this.searchValue);
        countData.append("departmentId", this.selectedDepartment);
        countData.append("initStatus", this.initStatusFilter);

        const countRes = await execute(countData, authUrl);
        if (!countRes.data.success) {
          throw new Error('Failed to get record count');
        }

        const totalRecords = countRes.data.data.totalCount;
        this.downloadProgress.totalRecords = totalRecords;
        this.downloadProgress.message = `Found ${totalRecords} records to download`;

        if (totalRecords === 0) {
          this.downloadProgress.status = 'No Data';
          this.downloadProgress.message = 'No records found to download';
          this.downloadProgress.percent = 100;
          return;
        }

        // Calculate pages needed (fetch 50 records per page for efficiency)
        const pageSize = 50;
        const totalPages = Math.ceil(totalRecords / pageSize);
        let currentPage = 1;
        let fetchedCount = 0;

        this.downloadProgress.status = 'Downloading...';

        // Fetch all pages
        while (currentPage <= totalPages && !this.downloadProgress.cancelled) {
          const pageData = new FormData();
          pageData.append("function", "getLeaveBalancesManagement");
          pageData.append("page", currentPage);
          pageData.append("limit", pageSize);
          pageData.append("year", this.selectedYear);
          pageData.append("search", this.searchValue);
          pageData.append("departmentId", this.selectedDepartment);
          pageData.append("initStatus", this.initStatusFilter);

          const pageRes = await execute(pageData, authUrl);

          if (pageRes.data.success && pageRes.data.data.employees) {
            this.allDownloadData.push(...pageRes.data.data.employees);
            fetchedCount += pageRes.data.data.employees.length;
          }

          // Update progress
          this.downloadProgress.fetched = fetchedCount;
          this.downloadProgress.percent = Math.round((currentPage / totalPages) * 100);
          this.downloadProgress.message = `Fetching page ${currentPage} of ${totalPages}...`;

          // Calculate time remaining
          const elapsed = Date.now() - this.downloadProgress.startTime;
          const avgTimePerPage = elapsed / currentPage;
          const remainingPages = totalPages - currentPage;
          const remainingMs = avgTimePerPage * remainingPages;
          this.downloadProgress.timeRemaining = this.formatTimeRemaining(remainingMs);

          currentPage++;

          // Small delay to prevent overwhelming the server
          await this.sleep(100);
        }

        if (this.downloadProgress.cancelled) {
          this.downloadProgress.status = 'Cancelled';
          this.downloadProgress.message = 'Download was cancelled';
          return;
        }

        // Generate CSV
        this.downloadProgress.status = 'Generating CSV...';
        this.downloadProgress.message = 'Creating downloadable file...';
        this.downloadProgress.percent = 95;

        await this.sleep(300);

        this.generateCSV();

        // Complete
        this.downloadProgress.status = 'Complete!';
        this.downloadProgress.message = `Successfully downloaded ${fetchedCount} records`;
        this.downloadProgress.percent = 100;
        this.downloadProgress.timeRemaining = '00:00';

      } catch (error) {
        console.error('Download error:', error);
        this.downloadProgress.status = 'Error';
        this.downloadProgress.message = error.message || 'Failed to download report';
        this.downloadProgress.percent = 0;
      }
    },

    closeDownloadProgressModal() {
      this.showDownloadProgressModal = false;
    },

    // Generate CSV from downloaded data
    generateCSV() {
      if (this.allDownloadData.length === 0) return;

      // Define CSV headers
      const headers = [
        'Employee No',
        'National ID',
        'Employee Name',
        'Department',
        'Position',
        'Total Leave Days',
        'Used Days',
        'Remaining Days',
        'Consumption %',
        'Status'
      ];

      // Map data to CSV rows
      const rows = this.allDownloadData.map(emp => {
        const total = parseFloat(emp.totalLeaveDays) || 0;
        const used = parseFloat(emp.usedLeaveDays) || 0;
        const remaining = parseFloat(emp.remainingLeaveDays) || 0;
        const consumption = total > 0 ? Math.round((used / total) * 100) : 0;

        return [
          emp.empNo || '',
          emp.nationalId || '',
          (emp.fullName || '').replace(/,/g, ' ').trim(),
          (emp.departmentName || '').replace(/,/g, ' '),
          (emp.positionTitle || '-').replace(/,/g, ' '),
          total.toFixed(1),
          used.toFixed(1),
          remaining.toFixed(1),
          consumption + '%',
          emp.initializationStatus || ''
        ];
      });

      // Create CSV content
      let csvContent = headers.join(',') + '\n';
      rows.forEach(row => {
        csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
      });

      // Create and download file
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `LeaveBalances_${this.selectedYear}_${this.formatDateForFilename()}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    },

    // Cancel download
    cancelDownload() {
      this.downloadProgress.cancelled = true;
    },

    // Format time remaining
    formatTimeRemaining(ms) {
      if (ms <= 0 || isNaN(ms)) return '00:00';

      const seconds = Math.ceil(ms / 1000);
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;

      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },

    // Format date for filename
    formatDateForFilename() {
      const now = new Date();
      return `${now.getFullYear()}${(now.getMonth()+1).toString().padStart(2,'0')}${now.getDate().toString().padStart(2,'0')}_${now.getHours().toString().padStart(2,'0')}${now.getMinutes().toString().padStart(2,'0')}`;
    },

    // Sleep helper
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    // Toggle select all
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedEmployees = [];
      } else {
        this.selectedEmployees = this.employees.map(emp => emp.nationalId);
      }
    },

    // Pagination methods
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchEmployees();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.fetchEmployees();
      }
    },

    goToPage(page) {
      if (page > 0 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.fetchEmployees();
      }
    },

    // Utility methods
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      const options = { day: 'numeric', month: 'short', year: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    formatEligibleGender(gender) {
      const map = {
        all: 'All employees',
        male: 'Male only',
        female: 'Female only'
      };
      return map[(gender || 'all').toLowerCase()] || 'All employees';
    },

    getInitials(name) {
      if (!name) return 'NA';
      return name.split(' ').filter(n => n).map(n => n[0]).join('').toUpperCase().slice(0, 2);
    },

    getStatusClass(status) {
      const classes = {
        'Initialized': 'hr-badge-success',
        'Partially Initialized': 'hr-badge-warning',
        'Not Initialized': 'hr-badge-danger'
      };
      return classes[status] || 'hr-badge-muted';
    },

    // Get consumption percentage
    getConsumptionPercent(emp) {
      const total = parseFloat(emp.totalLeaveDays) || 0;
      const used = parseFloat(emp.usedLeaveDays) || 0;
      if (total === 0) return 0;
      return Math.min(Math.round((used / total) * 100), 100);
    },

    // Get progress bar color class based on consumption
    getProgressBarClass(emp) {
      const percent = this.getConsumptionPercent(emp);
      if (percent >= 90) return 'bg-danger';
      if (percent >= 75) return 'bg-warning';
      if (percent >= 50) return 'bg-info';
      return 'bg-success';
    },

    getRemainingDaysClass(emp) {
      const remaining = parseFloat(emp.remainingLeaveDays) || 0;
      const total = parseFloat(emp.totalLeaveDays) || 0;
      if (total === 0) return 'remaining-muted';
      const percent = (remaining / total) * 100;
      if (percent <= 10) return 'remaining-danger';
      if (percent <= 25) return 'remaining-warning';
      return 'remaining-success';
    },

    getLeaveStatusClass(status) {
      const classes = {
        'Approved': 'bg-success',
        'Pending': 'bg-warning',
        'Rejected': 'bg-danger'
      };
      return classes[status] || 'bg-secondary';
    },

    getRemainingClass(balance) {
      const remaining = parseFloat(balance.remainingDays) || 0;
      const total = parseFloat(balance.totalDays) || 0;

      if (balance.status !== 'Active') return 'bg-secondary';
      if (remaining <= 0) return 'bg-danger';
      if (remaining < total * 0.25) return 'bg-warning';
      return 'bg-success';
    },

    showSuccess(message) {
      this.successMessage = message;
      this.$nextTick(() => this.$refs.successModal?.open());
    },

    showError(message) {
      this.errorMessage = message;
      this.$nextTick(() => this.$refs.errorModal?.open());
    },

    // =====================
    // CARRY FORWARD METHODS
    // =====================

    // Open bulk carry forward modal (method name avoids collision with data property showCarryForwardModal)
    openCarryForwardModal() {
      if (this.selectedEmployees.length === 0) {
        this.showError('Please select at least one employee');
        return;
      }
      this.carryForwardForm.fromYear = this.selectedYear - 1;
      this.carryForwardForm.toYear = this.selectedYear;
      this.carryForwardPreview = [];
      this.totalEligibleDays = 0;
      this.showCarryForwardModal = true;
      this.addCarryForwardEscapeListener();

      this.$nextTick(() => {
        this.$refs.carryForwardOverlay?.focus?.();
      });

      // Load preview if single employee selected
      if (this.selectedEmployees.length === 1) {
        this.loadCarryForwardPreview();
      }
    },

    closeCarryForwardModal() {
      this.showCarryForwardModal = false;
      this.removeCarryForwardEscapeListener();
    },

    onCarryForwardOverlayClick(e) {
      if (e.target === e.currentTarget) {
        this.closeCarryForwardModal();
      }
    },

    _carryForwardEscapeHandler(e) {
      if (e.key === 'Escape' && this.showCarryForwardModal) {
        this.closeCarryForwardModal();
      }
    },

    addCarryForwardEscapeListener() {
      this.removeCarryForwardEscapeListener();
      window.addEventListener('keydown', this._carryForwardEscapeHandler);
    },

    removeCarryForwardEscapeListener() {
      window.removeEventListener('keydown', this._carryForwardEscapeHandler);
    },

    // Load carry forward preview for bulk (single employee)
    async loadCarryForwardPreview() {
      if (this.selectedEmployees.length !== 1) return;

      this.previewLoading = true;
      const data = new FormData();
      data.append("function", "getCarryForwardPreview");
      data.append("nationalId", this.selectedEmployees[0]);
      data.append("fromYear", this.carryForwardForm.fromYear);
      data.append("toYear", this.carryForwardForm.toYear);
      data.append("maxCarryForward", this.carryForwardForm.maxDays);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.carryForwardPreview = res.data.data.preview;
          this.totalEligibleDays = res.data.data.totalEligibleDays;
        }
      } catch (error) {
        console.error('Error loading preview:', error);
      } finally {
        this.previewLoading = false;
      }
    },

    // Execute bulk carry forward
    async executeCarryForward() {
      if (this.carryForwardForm.fromYear >= this.carryForwardForm.toYear) {
        this.showError('Target year must be greater than source year');
        return;
      }

      this.carryingForward = true;

      const data = new FormData();
      data.append("function", "bulkCarryForwardLeaveBalances");
      data.append("nationalIds", JSON.stringify(this.selectedEmployees));
      data.append("fromYear", String(this.carryForwardForm.fromYear));
      data.append("toYear", String(this.carryForwardForm.toYear));
      data.append("maxCarryForward", String(this.carryForwardForm.maxDays || 5));

      try {
        const res = await execute(data, authUrl);
        const success = res?.data?.success === true;
        const message = res?.data?.message || (success ? 'Carry forward complete.' : 'Carry forward failed.');
        if (success) {
          this.closeCarryForwardModal();
          this.showSuccess(message);
          this.selectedEmployees = [];
          await this.fetchEmployees();
        } else {
          this.showError(message);
        }
      } catch (error) {
        console.error('Error carrying forward:', error);
        this.showError(error?.response?.data?.message || 'Failed to carry forward leave balances');
      } finally {
        this.carryingForward = false;
      }
    },

    // Open single employee carry forward modal
    openSingleCarryForward() {
      this.singleCarryForm.fromYear = this.selectedYear - 1;
      this.singleCarryForm.toYear = this.selectedYear;
      this.singleCarryPreview = [];
      this.singleTotalEligible = 0;

      this.showDetailsModal = false;
      setTimeout(() => {
        this.loadSingleCarryPreview();
        this.showSingleCarryForwardModal = true;
      }, 50);
    },

    closeSingleCarryForwardModal() {
      this.showSingleCarryForwardModal = false;
      setTimeout(() => {
        this.viewEmployeeDetails(this.selectedEmployee);
      }, 50);
    },

    // Load single employee carry forward preview
    async loadSingleCarryPreview() {
      if (!this.selectedEmployee.nationalId) return;

      this.singlePreviewLoading = true;
      const data = new FormData();
      data.append("function", "getCarryForwardPreview");
      data.append("nationalId", this.selectedEmployee.nationalId);
      data.append("fromYear", this.singleCarryForm.fromYear);
      data.append("toYear", this.singleCarryForm.toYear);
      data.append("maxCarryForward", this.singleCarryForm.maxDays);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.singleCarryPreview = res.data.data.preview;
          this.singleTotalEligible = res.data.data.totalEligibleDays;
        }
      } catch (error) {
        console.error('Error loading preview:', error);
      } finally {
        this.singlePreviewLoading = false;
      }
    },

    // Execute single employee carry forward
    async executeSingleCarryForward() {
      if (this.singleCarryForm.fromYear >= this.singleCarryForm.toYear) {
        this.showError('Target year must be greater than source year');
        return;
      }

      this.singleCarryingForward = true;

      const data = new FormData();
      data.append("function", "carryForwardLeaveBalances");
      data.append("nationalId", this.selectedEmployee.nationalId);
      data.append("fromYear", this.singleCarryForm.fromYear);
      data.append("toYear", this.singleCarryForm.toYear);
      data.append("maxCarryForward", this.singleCarryForm.maxDays);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.showSingleCarryForwardModal = false;
          this.showSuccess(res.data.message);
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error carrying forward:', error);
        this.showError('Failed to carry forward leave balances');
      } finally {
        this.singleCarryingForward = false;
      }
    },

    // =====================
    // ROLLBACK METHODS
    // =====================

    // Open rollback modal
    openRollbackModal() {
      this.rollbackForm.year = this.selectedYear;
      this.rollbackForm.reason = '';
      this.rollbackPreview = [];
      this.totalCarriedOverToRollback = 0;

      this.showDetailsModal = false;
      setTimeout(() => {
        this.loadRollbackPreview();
        this.showRollbackModal = true;
      }, 50);
    },

    closeRollbackModal() {
      this.showRollbackModal = false;
      setTimeout(() => {
        this.viewEmployeeDetails(this.selectedEmployee);
      }, 50);
    },

    // Load rollback preview
    async loadRollbackPreview() {
      if (!this.selectedEmployee.nationalId) return;

      this.rollbackPreviewLoading = true;
      const data = new FormData();
      data.append("function", "getRollbackPreview");
      data.append("nationalId", this.selectedEmployee.nationalId);
      data.append("year", this.rollbackForm.year);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.rollbackPreview = res.data.data.preview;
          this.totalCarriedOverToRollback = res.data.data.totalCarriedOver;
        }
      } catch (error) {
        console.error('Error loading rollback preview:', error);
      } finally {
        this.rollbackPreviewLoading = false;
      }
    },

    // Execute rollback
    async executeRollback() {
      if (!this.rollbackForm.reason) {
        this.showError('Please provide a reason for the rollback');
        return;
      }

      this.rollingBack = true;

      const data = new FormData();
      data.append("function", "rollbackCarryForward");
      data.append("nationalId", this.selectedEmployee.nationalId);
      data.append("year", this.rollbackForm.year);
      data.append("reason", this.rollbackForm.reason);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.showRollbackModal = false;
          this.showSuccess(res.data.message);
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error executing rollback:', error);
        this.showError('Failed to rollback carry forward');
      } finally {
        this.rollingBack = false;
      }
    },

    // =====================
    // CASH OUT METHODS
    // =====================

    // Show bulk cash out modal
    showCashOutModal() {
      if (this.selectedEmployees.length === 0) {
        this.showError('Please select at least one employee');
        return;
      }
      this.bulkCashOutForm = {
        year: this.selectedYear,
        leaveTypeId: '',
        maxDays: '',
        amountPerDay: 0,
        paymentReference: '',
        remarks: ''
      };
      this.showBulkCashoutModal = true;
    },

    closeBulkCashoutModal() {
      this.showBulkCashoutModal = false;
    },

    // Open single employee cash out modal
    openSingleCashOutModal() {
      this.cashOutForm = {
        year: this.selectedYear,
        leaveTypeId: '',
        days: 0,
        maxDays: 0,
        amountPerDay: 0,
        paymentReference: '',
        remarks: ''
      };
      this.cashOutBalances = [];
      this.suggestedDailyRate = 0;

      this.showDetailsModal = false;
      setTimeout(() => {
        this.loadCashOutPreview();
        this.showSingleCashoutModal = true;
      }, 50);
    },

    closeSingleCashoutModal() {
      this.showSingleCashoutModal = false;
      setTimeout(() => {
        this.viewEmployeeDetails(this.selectedEmployee);
      }, 50);
    },

    // Load cash out preview
    async loadCashOutPreview() {
      if (!this.selectedEmployee.nationalId) return;

      this.cashOutPreviewLoading = true;
      const data = new FormData();
      data.append("function", "getCashOutPreview");
      data.append("nationalId", this.selectedEmployee.nationalId);
      data.append("year", this.cashOutForm.year);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.cashOutBalances = res.data.data.balances;
          this.suggestedDailyRate = res.data.data.suggestedDailyRate || 0;
          if (this.suggestedDailyRate > 0) {
            this.cashOutForm.amountPerDay = this.suggestedDailyRate;
          }
        }
      } catch (error) {
        console.error('Error loading cash out preview:', error);
      } finally {
        this.cashOutPreviewLoading = false;
      }
    },

    // Handle leave type change in cash out form
    onLeaveTypeChange() {
      const selected = this.cashOutBalances.find(b => b.leaveTypeID === this.cashOutForm.leaveTypeId);
      if (selected) {
        this.cashOutForm.maxDays = parseFloat(selected.remainingDays);
        this.cashOutForm.days = parseFloat(selected.remainingDays);
      } else {
        this.cashOutForm.maxDays = 0;
        this.cashOutForm.days = 0;
      }
    },

    // Execute single cash out
    async executeCashOut() {
      if (!this.cashOutForm.leaveTypeId || this.cashOutForm.days <= 0) {
        this.showError('Please select a leave type and enter days to cash out');
        return;
      }

      if (this.cashOutForm.days > this.cashOutForm.maxDays) {
        this.showError(`Cannot cash out more than ${this.cashOutForm.maxDays} days`);
        return;
      }

      this.cashingOut = true;

      const data = new FormData();
      data.append("function", "cashOutLeave");
      data.append("nationalId", this.selectedEmployee.nationalId);
      data.append("year", this.cashOutForm.year);
      data.append("leaveTypeId", this.cashOutForm.leaveTypeId);
      data.append("days", this.cashOutForm.days);
      data.append("amountPerDay", this.cashOutForm.amountPerDay);
      data.append("paymentReference", this.cashOutForm.paymentReference);
      data.append("remarks", this.cashOutForm.remarks);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.showSingleCashoutModal = false;
          this.showSuccess(res.data.message);
          await this.fetchEmployees();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error cashing out:', error);
        this.showError('Failed to process cash out');
      } finally {
        this.cashingOut = false;
      }
    },

    // Execute bulk cash out
    async executeBulkCashOut() {
      if (!this.bulkCashOutForm.leaveTypeId) {
        this.showError('Please select a leave type');
        return;
      }

      this.bulkCashingOut = true;

      const data = new FormData();
      data.append("function", "bulkCashOutLeave");
      data.append("nationalIds", JSON.stringify(this.selectedEmployees));
      data.append("year", String(this.bulkCashOutForm.year || new Date().getFullYear()));
      data.append("leaveTypeId", String(this.bulkCashOutForm.leaveTypeId));
      data.append("maxDays", this.bulkCashOutForm.maxDays != null && this.bulkCashOutForm.maxDays !== '' ? String(this.bulkCashOutForm.maxDays) : '');
      data.append("amountPerDay", String(parseFloat(this.bulkCashOutForm.amountPerDay) || 0));
      data.append("paymentReference", String(this.bulkCashOutForm.paymentReference || '').trim());
      data.append("remarks", String(this.bulkCashOutForm.remarks || '').trim());

      try {
        const res = await execute(data, authUrl);
        const success = res?.data?.success === true;
        const message = res?.data?.message || (success ? 'Bulk cash out complete.' : 'Bulk cash out failed.');
        if (success) {
          this.showBulkCashoutModal = false;
          this.showSuccess(message);
          this.selectedEmployees = [];
          await this.fetchEmployees();
        } else {
          this.showError(message);
        }
      } catch (error) {
        console.error('Error bulk cashing out:', error);
        this.showError(error?.response?.data?.message || 'Failed to process bulk cash out');
      } finally {
        this.bulkCashingOut = false;
      }
    },

    // Format currency
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(amount || 0);
    }
  }
};
</script>

<style scoped>
/* Add custom modal styles */
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  overflow-y: auto;
  padding: 1rem;
  pointer-events: auto;
  outline: none;
}

.carry-forward-overlay {
  z-index: 1060;
}

.custom-modal {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 2rem);
  width: 100%;
  max-width: 500px;
  animation: modalFadeIn 0.3s ease;
}

.custom-modal-xl {
  max-width: 1140px;
}

.custom-modal-lg {
  max-width: 800px;
}

.custom-modal-centered {
  max-width: 500px;
}

.custom-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
}

.custom-modal-header.bg-primary { background-color: #4e73df; }
.custom-modal-header.bg-warning { background-color: #f6c23e; }
.custom-modal-header.bg-info { background-color: #36b9cc; }
.custom-modal-header.bg-success { background-color: #1cc88a; }
.custom-modal-header.bg-danger { background-color: #e74a3b; }

.custom-modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
}

.custom-modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: white;
  cursor: pointer;
  opacity: 0.7;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.custom-modal-close:hover {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.1);
}

.custom-modal-body {
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
}

.custom-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  gap: 0.5rem;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Keep all your existing styles */
.avatar-initials {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 10px;
  background: #dbeafe;
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
}

.emp-name { font-weight: 600; font-size: 14px; }
.emp-meta { font-size: 12px; color: #64748b; display: block; }
.text-primary-bold { font-weight: 700; color: #3b82f6; }
.text-warning-bold { font-weight: 700; color: #f59e0b; }
.remaining-muted { color: #94a3b8; font-weight: 600; }
.remaining-danger { color: #ef4444; font-weight: 600; }
.remaining-warning { color: #f59e0b; font-weight: 600; }
.remaining-success { color: #10b981; font-weight: 600; }
.consumption-wrapper { min-width: 120px; }
.hr-checkbox { width: 18px; height: 18px; accent-color: #4f46e5; cursor: pointer; }
.success-icon, .error-icon { font-size: 48px; margin-bottom: 12px; }
.success-icon { color: #10b981; }
.error-icon { color: #ef4444; }
.success-heading { color: #10b981; margin: 0 0 8px 0; }
.error-heading { color: #ef4444; margin: 0 0 8px 0; }

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #74788d;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2rem;
  width: 2rem;
}

.bg-soft-primary {
  background-color: rgba(85, 110, 230, 0.1);
}

.bg-soft-info {
  background-color: rgba(52, 195, 143, 0.1);
}

.bg-soft-secondary {
  background-color: rgba(116, 120, 141, 0.1);
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
}

.page-item.disabled .page-link {
  cursor: not-allowed;
}

.table th {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #495057;
  white-space: nowrap;
}

.form-check-input {
  cursor: pointer;
}

.modal-xl {
  max-width: 1140px;
}

/* Progress bar styles */
.consumption-wrapper {
  min-width: 180px;
}

.progress {
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar {
  transition: width 0.6s ease;
  border-radius: 4px;
}

.progress-bar.bg-success {
  background: linear-gradient(90deg, #34c38f 0%, #2ecc71 100%) !important;
}

.progress-bar.bg-info {
  background: linear-gradient(90deg, #50a5f1 0%, #3498db 100%) !important;
}

.progress-bar.bg-warning {
  background: linear-gradient(90deg, #f1b44c 0%, #f39c12 100%) !important;
}

.progress-bar.bg-danger {
  background: linear-gradient(90deg, #f46a6a 0%, #e74c3c 100%) !important;
}

/* Table row hover effect */
.table-hover tbody tr:hover {
  background-color: rgba(85, 110, 230, 0.05);
}

/* Badge styles */
.badge.bg-soft-secondary {
  color: #74788d;
}

/* Stats cards animation */
.mini-stats-wid {
  transition: transform 0.2s ease;
}

.mini-stats-wid:hover {
  transform: translateY(-2px);
}

/* Download progress modal */
.download-icon-wrapper {
  display: inline-block;
  animation: bounce 1s infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

#download-progress-modal .progress {
  border-radius: 10px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
}

#download-progress-modal .progress-bar {
  font-weight: 600;
  font-size: 0.85rem;
  line-height: 20px;
}

#download-progress-modal .modal-body h5 {
  color: #495057;
}

#download-progress-modal .border-end {
  border-color: #e9ecef !important;
}

/* Keep all Bootstrap utility classes */
.text-white { color: #fff; }
.bg-primary { background-color: #4e73df; }
.bg-warning { background-color: #f6c23e; }
.bg-info { background-color: #36b9cc; }
.bg-success { background-color: #1cc88a; }
.bg-danger { background-color: #e74a3b; }
.bg-secondary { background-color: #858796; }
.btn { display: inline-block; font-weight: 400; text-align: center; vertical-align: middle; cursor: pointer; padding: 0.375rem 0.75rem; font-size: 1rem; line-height: 1.5; border-radius: 0.35rem; transition: color 0.15s; }
.btn-sm { padding: 0.25rem 0.5rem; font-size: 0.875rem; border-radius: 0.25rem; }
.btn-primary { color: #fff; background-color: #4e73df; border: 1px solid #4e73df; }
.btn-secondary { color: #fff; background-color: #858796; border: 1px solid #858796; }
.btn-success { color: #fff; background-color: #1cc88a; border: 1px solid #1cc88a; }
.btn-danger { color: #fff; background-color: #e74a3b; border: 1px solid #e74a3b; }
.btn-warning { color: #fff; background-color: #f6c23e; border: 1px solid #f6c23e; }
.btn-info { color: #fff; background-color: #36b9cc; border: 1px solid #36b9cc; }
.btn-outline-primary { color: #4e73df; border: 1px solid #4e73df; background: transparent; }
.btn-outline-danger { color: #e74a3b; border: 1px solid #e74a3b; background: transparent; }
.form-control { display: block; width: 100%; padding: 0.375rem 0.75rem; font-size: 1rem; line-height: 1.5; color: #6e707e; background-color: #fff; border: 1px solid #d1d3e2; border-radius: 0.35rem; }
.form-select { display: block; width: 100%; padding: 0.375rem 1.75rem 0.375rem 0.75rem; font-size: 1rem; line-height: 1.5; color: #6e707e; background-color: #fff; border: 1px solid #d1d3e2; border-radius: 0.35rem; }
.table { width: 100%; margin-bottom: 1rem; color: #858796; border-collapse: collapse; }
.table-bordered { border: 1px solid #e3e6f0; }
.table-striped tbody tr:nth-of-type(odd) { background-color: rgba(0,0,0,.05); }
.table-light { background-color: #f8f9fc; }
.badge { display: inline-block; padding: 0.25em 0.4em; font-size: 75%; font-weight: 700; line-height: 1; text-align: center; white-space: nowrap; vertical-align: baseline; border-radius: 0.35rem; }
.alert { position: relative; padding: 0.75rem 1.25rem; margin-bottom: 1rem; border: 1px solid transparent; border-radius: 0.35rem; }
.alert-info { color: #0c5460; background-color: #d1ecf1; border-color: #bee5eb; }
.alert-warning { color: #856404; background-color: #fff3cd; border-color: #ffeeba; }
.alert-danger { color: #721c24; background-color: #f8d7da; border-color: #f5c6cb; }
.row { display: flex; flex-wrap: wrap; margin-right: -0.75rem; margin-left: -0.75rem; }
.col-md-3, .col-md-4, .col-md-6 { position: relative; width: 100%; padding-right: 0.75rem; padding-left: 0.75rem; }
.col-md-3 { flex: 0 0 25%; max-width: 25%; }
.col-md-4 { flex: 0 0 33.33333%; max-width: 33.33333%; }
.col-md-6 { flex: 0 0 50%; max-width: 50%; }
.text-center { text-align: center; }
.text-primary { color: #4e73df; }
.text-danger { color: #e74a3b; }
.text-muted { color: #858796; }
.fw-medium { font-weight: 500; }
.mb-0 { margin-bottom: 0; }
.mb-1 { margin-bottom: 0.25rem; }
.mb-2 { margin-bottom: 0.5rem; }
.mb-3 { margin-bottom: 1rem; }
.mb-4 { margin-bottom: 1.5rem; }
.mt-3 { margin-top: 1rem; }
.mt-4 { margin-top: 1.5rem; }
.me-1 { margin-right: 0.25rem; }
.me-2 { margin-right: 0.5rem; }
.py-3 { padding-top: 1rem; padding-bottom: 1rem; }
.py-4 { padding-top: 1.5rem; padding-bottom: 1.5rem; }
.border-end { border-right: 1px solid #e3e6f0; }
.spinner-border { display: inline-block; width: 1rem; height: 1rem; vertical-align: text-bottom; border: 0.2em solid currentColor; border-right-color: transparent; border-radius: 50%; animation: spinner-border .75s linear infinite; }
.spinner-border-sm { width: 0.8rem; height: 0.8rem; border-width: 0.15em; }
@keyframes spinner-border { to { transform: rotate(360deg); } }
</style>